import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiHome, FiFolder, FiFile, FiMessageSquare, FiDownload, FiSearch, FiX, FiUploadCloud, FiChevronLeft, FiChevronRight, FiEye } from "react-icons/fi";
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFile, FaVideo, FaClock } from "react-icons/fa";
import { IoMdNotificationsOutline } from "react-icons/io";
import { useTheme } from "../../context/SocketContext";
import { useRealtimeRefresh } from "../../context/RealtimeSyncContext";
import FilePreviewModal from "../Tax Filling/FilePreviewModal";
import PageHeader from "../../components/PageHeader";
import { TableSkeleton } from "../../components/ui/Skeleton";
import EmptyState from "../../components/ui/EmptyState";
import { useDocumentsPortal, getDocumentsPageMeta, getPortalIcon } from "../../portal";

const UserDocumentsPage = () => {
  const { currentThemeColors } = useTheme();
  const { page: documentsPageContent } = useDocumentsPortal();
  const pageMeta = getDocumentsPageMeta(documentsPageContent);
  const PageIcon = getPortalIcon(pageMeta.pageIcon);
  const MainTabIcon = getPortalIcon(pageMeta.mainTabIcon);
  const ChatTabIcon = getPortalIcon(pageMeta.chatTabIcon);
  const [activeTab, setActiveTab] = useState("main");
  const [documents, setDocuments] = useState([]);
  const [chatDocuments, setChatDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [filteredChatDocs, setFilteredChatDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [previewFile, setPreviewFile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const sortDocumentsByDate = (docs) => {
    return [...docs].sort((a, b) => {
      const dateA = new Date(a.date || a.timestamp);
      const dateB = new Date(b.date || b.timestamp);
      return dateB - dateA;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  };

  const buildDownloadUrl = (url) => {
    if (!url) return "#";
    let final = url.trim();
    // Clean legacy localhost prefix in stored URL
    final = final.replace(/http:\/\/localhost:5000\//, "/");
    // Clean legacy 'undefined' prefix in stored URL
    final = final.replace(/^undefined\/?/, "/");
    const isAbsolute = /^https?:\/\//i.test(final);
    if (isAbsolute) return final;
    const base = process.env.REACT_APP_API_BASE_URL || "";
    if (final.startsWith("/")) return `${base}${final}`;
    // Fallback: treat as relative uploads path
    return `${base}/${final}`;
  };

  const isNewDocument = (docDate) => {
    const docTime = new Date(docDate).getTime();
    const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return docTime > weekAgo;
  };

  const fetchDocuments = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch user data");

      const data = await response.json();
      const sortedDocs = sortDocumentsByDate((data.user.documents || []).filter(doc => !doc.isDraft));
      setDocuments(sortedDocs);
      setFilteredDocs(sortedDocs);
      setFolders(data.user.folders || []);

      const chatResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/documents?userId=${data.user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const activeChatDocs = chatResponse.ok ? await chatResponse.json() : [];

      const yearsResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/chat/available-years?userId=${data.user._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let archivedChatDocs = [];
      if (yearsResponse.ok) {
        const years = await yearsResponse.json();

        for (const yearData of years) {
          const archivedDocsResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-documents?userId=${data.user._id}&year=${yearData.year}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (archivedDocsResponse.ok) {
            const yearDocs = await archivedDocsResponse.json();
            archivedChatDocs = [...archivedChatDocs, ...yearDocs];
          }
        }
      }

      const allChatDocs = [...activeChatDocs, ...archivedChatDocs];
      const sortedChatDocs = sortDocumentsByDate(allChatDocs);
      setChatDocuments(sortedChatDocs);
      setFilteredChatDocs(sortedChatDocs);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Safety check to unlock body scroll that might be stuck from modals
    document.body.style.overflow = "";
    fetchDocuments();
  }, [fetchDocuments]);

  useRealtimeRefresh('documents', fetchDocuments);

  useEffect(() => {
    if (location.state?.openFolder && folders.length > 0) {
      const folder = folders.find(f => f.name === location.state.openFolder);
      if (folder) {
        setCurrentFolder(folder._id);
        setFolderPath([folder]);
        // Clear state to avoid re-opening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, folders, navigate, location.pathname]);

  useEffect(() => {
    setCurrentFolder(null);
    setFolderPath([]);
  }, [activeTab]);

  const matchesSearch = (text) => {
    if (!searchTerm.trim()) return true;
    return (text || "").toLowerCase().includes(searchTerm.toLowerCase().trim());
  };

  useEffect(() => {
    if (activeTab === "main") {
      const filtered = documents.filter(doc =>
        (currentFolder ? doc.folderId?.toString() === currentFolder.toString() : !doc.folderId) &&
        matchesSearch(doc.originalName)
      );
      setFilteredDocs(filtered);
    } else {
      const filtered = chatDocuments.filter((doc) =>
        (currentFolder ? doc.folderId?.toString() === currentFolder.toString() : !doc.folderId) &&
        matchesSearch(doc.originalName)
      );
      setFilteredChatDocs(filtered);
    }
    setPage(1);
  }, [activeTab, documents, chatDocuments, currentFolder, searchTerm]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const openFolder = (folder) => {
    setCurrentFolder(folder._id);
    setFolderPath([...folderPath, folder]);
  };

  const navigateToFolder = (index) => {
    if (index === -1) {
      setCurrentFolder(null);
      setFolderPath([]);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[newPath.length - 1]._id);
    }
  };

  const getFolderSection = (folder) => folder?.section || "documents";

  const getActiveSection = () => (activeTab === "main" ? "documents" : activeTab === "chat" ? "chat" : "documents");

  const getSectionFolders = () => folders.filter((f) => (f.section || "documents") === getActiveSection());

  const showFolderNav = (activeTab === "main" || activeTab === "chat") && (getSectionFolders().length > 0 || currentFolder);

  const getCurrentFolderContents = () => {
    const section = getActiveSection();
    const sectionFolders = folders.filter((f) => getFolderSection(f) === section);
    if (!currentFolder) return sectionFolders.filter((f) => !f.parentFolder);
    const currentId = currentFolder.toString();
    return sectionFolders.filter((f) => f.parentFolder?.toString() === currentId);
  };

  const currentDocs = activeTab === "main" ? filteredDocs : filteredChatDocs;
  const count = Math.ceil(currentDocs.length / rowsPerPage);
  const paginatedDocs = currentDocs.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getFileIcon = (fileName, isMeetingRecording) => {
    if (isMeetingRecording) {
      return <FaVideo className="w-6 h-6 text-purple-600" />;
    }
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return <FaFilePdf className="w-6 h-6 text-red-500" />;
      case 'doc':
      case 'docx': return <FaFileWord className="w-6 h-6 text-blue-500" />;
      case 'xls':
      case 'xlsx': return <FaFileExcel className="w-6 h-6 text-green-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <FaFileImage className="w-6 h-6 text-purple-500" />;
      case 'mp4':
      case 'webm':
      case 'avi':
      case 'mov': return <FaVideo className="w-6 h-6 text-purple-600" />;
      default: return <FaFile className="w-6 h-6 text-gray-500" />;
    }
  };

  const getExpiryInfo = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { text: 'Expired', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20' };
    if (daysLeft <= 7) return { text: `${daysLeft} days left`, color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20' };
    if (daysLeft <= 30) return { text: `${daysLeft} days left`, color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20' };
    return { text: `${daysLeft} days left`, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20' };
  };

  const renderFolders = () => {
    const folderContents = getCurrentFolderContents().filter((folder) => matchesSearch(folder.name));
    if (folderContents.length === 0) return null;

    return (
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
          <FiFolder className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500" />
          <h3 className="section-title">{pageMeta.foldersTitle}</h3>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
          {folderContents.map((folder) => (
            <div
              key={folder._id}
              onClick={() => openFolder(folder)}
              className="group cursor-pointer flex items-center gap-2 sm:gap-3 px-2.5 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl bg-white dark:bg-dark-800 border-2 border-gray-200 dark:border-dark-600 hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-md sm:hover:shadow-lg transition-all duration-200 w-full sm:w-[180px] sm:min-h-[72px]"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-md sm:rounded-lg border border-primary-200 dark:border-primary-800 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 flex items-center justify-center sm:group-hover:scale-110 transition-transform duration-200">
                <FiFolder className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <span className="text-xs sm:text-sm font-semibold text-dark-700 dark:text-gray-300 line-clamp-2 leading-snug min-w-0">{folder.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDesktopView = (docs) => (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-700 border-b border-gray-100 dark:border-dark-600">
                <th className="px-6 py-4 text-left table-th">{pageMeta.tableDocumentLabel}</th>
                <th className="px-6 py-4 text-left table-th">{pageMeta.tableDateLabel}</th>
                <th className="px-6 py-4 text-right table-th">{pageMeta.tableActionsLabel}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {docs.map((document, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                          {getFileIcon(document.originalName, document.isMeetingRecording)}
                        </div>
                        {isNewDocument(document.date || document.timestamp) && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary-500 rounded-full flex items-center justify-center">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-dark-800 dark:text-white line-clamp-1">{document.originalName}</p>
                          {document.isMeetingRecording && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-medium">
                              <FaVideo className="w-3 h-3" />
                              Meeting Recording
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(document.size)}</p>
                          {document.isMeetingRecording && document.meetingTitle && (
                            <>
                              <span className="text-gray-400">•</span>
                              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{document.meetingTitle}</p>
                            </>
                          )}
                        </div>
                        {document.isMeetingRecording && document.expiresAt && (
                          <div className="flex items-center gap-1 mt-1">
                            <FaClock className="w-3 h-3 text-gray-400" />
                            <span className={`text-xs font-medium ${getExpiryInfo(document.expiresAt)?.color}`}>
                              Auto-deletes in {getExpiryInfo(document.expiresAt)?.text}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(document.date || document.timestamp).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setPreviewFile({
                          url: document.fileUrl,
                          name: document.originalName,
                          size: document.size,
                          type: document.originalName?.split('.').pop()?.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg' // Fallback to image if not pdf, FilePreviewModal handles it
                        })}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                        title="Preview"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                      <a
                        href={buildDownloadUrl(document.fileUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium text-sm hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                      >
                        <FiDownload className="w-4 h-4" />
                        Download
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {docs.length === 0 && (
          <EmptyState
            icon={FiFile}
            title={pageMeta.emptyTitle}
            description={pageMeta.emptySubtitle}
            containerClassName="py-16 text-center"
            iconBox
            titleClassName="text-lg font-medium text-dark-700 dark:text-gray-300"
            descriptionClassName="text-sm text-gray-500 dark:text-gray-400 mt-1"
          />
        )}
      </div>
      {docs.length > 0 && count > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {(page - 1) * rowsPerPage + 1} to {Math.min(page * rowsPerPage, currentDocs.length)} of {currentDocs.length} documents
          </p>
          <div className="flex items-center gap-2">
            <button 
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            {Array.from({ length: Math.min(count, 5) }, (_, i) => {
              let pageNum;
              if (count <= 5) pageNum = i + 1;
              else if (page <= 3) pageNum = i + 1;
              else if (page >= count - 2) pageNum = count - 4 + i;
              else pageNum = page - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-10 h-10 rounded-lg font-medium text-sm transition-colors ${
                    page === pageNum
                      ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                      : 'bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button 
              disabled={page === count}
              onClick={() => handlePageChange(page + 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderMobileView = (docs) => (
    <>
      <div className="space-y-2 sm:space-y-3">
        {docs.map((document, index) => (
          <div key={index} className="card p-3 sm:p-4">
            <div className="flex items-center gap-2.5 sm:gap-3 flex-nowrap">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5 sm:[&>svg]:w-6 sm:[&>svg]:h-6">
                  {getFileIcon(document.originalName, document.isMeetingRecording)}
                </div>
                {isNewDocument(document.date || document.timestamp) && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-secondary-500 rounded-full flex items-center justify-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-dark-800 dark:text-white line-clamp-1 text-xs sm:text-sm">{document.originalName}</p>
                  {document.isMeetingRecording && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-medium">
                      <FaVideo className="w-3 h-3" />
                      Recording
                    </span>
                  )}
                </div>
                {document.isMeetingRecording && document.meetingTitle && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1">{document.meetingTitle}</p>
                )}
                <div className="flex items-center gap-1.5 mt-0.5 text-xs sm:text-xs text-gray-500 dark:text-gray-400">
                  <span className="truncate">{formatFileSize(document.size)}</span>
                  <span>•</span>
                  <span className="truncate">
                    {new Date(document.date || document.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                {document.isMeetingRecording && document.expiresAt && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <FaClock className="w-3 h-3 text-gray-400" />
                    <span className={`text-xs sm:text-xs font-medium ${getExpiryInfo(document.expiresAt)?.color}`}>
                      {getExpiryInfo(document.expiresAt)?.text}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-row flex-nowrap items-center gap-1.5 shrink-0">
                <button
                  onClick={() => setPreviewFile({
                    url: document.fileUrl,
                    name: document.originalName,
                    size: document.size,
                    type: document.originalName?.split('.').pop()?.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg'
                  })}
                  className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                  title="Preview"
                >
                  <FiEye className="w-4 h-4" />
                </button>
                <a
                  href={buildDownloadUrl(document.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1.5 sm:px-2.5 sm:py-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors text-xs sm:text-xs font-medium whitespace-nowrap"
                >
                  <FiDownload className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                  Download
                </a>
              </div>
            </div>
          </div>
        ))}
        {docs.length === 0 && (
          <EmptyState
            icon={FiFile}
            title={pageMeta.emptyTitle}
            description={searchTerm ? 'Try a different search term' : pageMeta.emptySubtitle}
            containerClassName="card py-12 text-center"
            iconBox
            titleClassName="text-lg font-medium text-dark-700 dark:text-gray-300"
            descriptionClassName="text-sm text-gray-500 dark:text-gray-400 mt-1"
          />
        )}
      </div>
      {docs.length > 0 && count > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {Math.min(page * rowsPerPage, currentDocs.length)} of {currentDocs.length}
          </p>
          <div className="flex items-center gap-2">
            <button 
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">Page {page} of {count}</span>
            <button 
              disabled={page === count}
              onClick={() => handlePageChange(page + 1)}
              className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        icon={PageIcon}
        title={pageMeta.pageTitle}
        subtitle={pageMeta.pageSubtitle}
      />
      
      {/* Tab Buttons + Search */}
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}
      >
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
        <button 
          onClick={() => {
            setActiveTab('main');
            setCurrentFolder(null);
            setFolderPath([]);
          }}
          className={`w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2 py-1.5 sm:px-5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-xs sm:text-base transition-all duration-300 sm:hover:scale-105 ${
            activeTab === 'main'
              ? 'text-white shadow-lg'
              : 'bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-600'
          }`}
          style={activeTab === 'main' ? { 
            background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
            boxShadow: `0 10px 15px -3px var(--glow-color)`
          } : {}}
        >
          <MainTabIcon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
          <span className="truncate">{pageMeta.mainTabLabel}</span>
        </button>
        <button 
          onClick={() => {
            setActiveTab('chat');
            setCurrentFolder(null);
            setFolderPath([]);
          }}
          className={`w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2 py-1.5 sm:px-5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-xs sm:text-base transition-all duration-300 sm:hover:scale-105 ${
            activeTab === 'chat'
              ? 'text-white shadow-lg'
              : 'bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-primary-600'
          }`}
          style={activeTab === 'chat' ? { 
            background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
            boxShadow: `0 10px 15px -3px var(--glow-color)`
          } : {}}
        >
          <ChatTabIcon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
          <span className="truncate">{pageMeta.chatTabLabel}</span>
        </button>
        </div>

        <div className="relative w-full sm:w-72 lg:w-80">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={pageMeta.searchPlaceholder}
            className="w-full pl-9 sm:pl-10 pr-9 sm:pr-10 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 text-xs sm:text-sm"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Clear search"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Folder Navigation - Main Documents & Chat Documents */}
      {showFolderNav && (
        <div className="space-y-4">
          {(folderPath.length > 0 || currentFolder) && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigateToFolder(folderPath.length <= 1 ? -1 : folderPath.length - 2)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-400 font-medium text-sm hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                <FiChevronLeft className="w-4 h-4" />
                {pageMeta.breadcrumbBackLabel}
              </button>
              <div className="flex items-center gap-1 text-sm">
                <button
                  onClick={() => navigateToFolder(-1)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                >
                  <FiHome className="w-4 h-4" />
                  <span>{pageMeta.breadcrumbHomeLabel}</span>
                </button>
                {folderPath.map((f, i) => (
                  <div key={f._id} className="flex items-center">
                    <span className="text-gray-400 dark:text-gray-500 mx-1">/</span>
                    <button
                      onClick={() => navigateToFolder(i)}
                      className="px-2 py-1 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                    >
                      {f.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {renderFolders()}
        </div>
      )}

      {/* Documents List */}
      <div>
        {loading ? (
          <TableSkeleton rows={6} columns={5} />
        ) : (
          <>
            <div className="hidden md:block">
              {renderDesktopView(paginatedDocs)}
            </div>
            <div className="md:hidden">
              {renderMobileView(paginatedDocs)}
            </div>
          </>
        )}
      </div>

      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
};

export default UserDocumentsPage;